import { AlertCircle, ShieldCheck } from "lucide-react";
import { useMemo } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaSelectField, type SelectOption } from "@/shared/components/forms/ColocaSelectField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";
import { formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";

import { CONCEPT_MAX_LENGTH } from "../../constants/payoutBatches.constants";
import { useBatchErrorMessage } from "../../hooks/useBatchErrorMessage";
import { useBatchSetupOptions } from "../../hooks/useBatchSetupOptions";
import { useTemplateDownload } from "../../hooks/useTemplateDownload";
import type { BatchError } from "../../utils/batchErrors";
import { type BatchSetupValues, buildBatchSetupSchema, splitCorridor } from "../../utils/batchSetupSchema";
import { BatchFileDropzone } from "./BatchFileDropzone";
import { CorridorChoiceField } from "./CorridorChoiceField";
import { CrossCurrencyNotice } from "./CrossCurrencyNotice";
import { OrganizationField } from "./OrganizationField";
import { TemplateDownloadCard } from "./TemplateDownloadCard";

export interface BatchSetupFormProps {
  isInternal: boolean;
  onSubmit: (values: BatchSetupValues) => void | Promise<void>;
  isSubmitting: boolean;
  submitError: BatchError | null;
}

/**
 * Step 1 of the wizard: where the money comes from (a wallet), where it goes (one corridor), the template to
 * fill and the filled file. Staff first say which organization they act for; nothing else loads until then.
 */
export function BatchSetupForm({ isInternal, onSubmit, isSubmitting, submitError }: BatchSetupFormProps) {
  const { t, activeLanguage } = useTranslations("payoutBatches");
  const describeError = useBatchErrorMessage();

  const schema = useMemo(
    () =>
      buildBatchSetupSchema({
        requireOrganization: isInternal,
        messages: { fileRequired: t("file_error_required") },
      }),
    [isInternal, t],
  );
  const form = useZodForm(schema, {
    organizationId: "",
    sourceCurrency: "",
    corridor: "",
    concept: "",
  });

  const organizationId = (form.watch("organizationId") as string | undefined) || null;
  const sourceCurrency = form.watch("sourceCurrency");
  const corridorRaw = form.watch("corridor");

  const options = useBatchSetupOptions(isInternal ? organizationId : null);
  const template = useTemplateDownload();

  const corridor = corridorRaw ? splitCorridor(corridorRaw) : null;
  const corridorOption = options.corridorOptions.find((option) => option.value === corridorRaw) ?? null;
  const locked = !options.canLoad;

  const walletItems: SelectOption[] = options.wallets.map((wallet) => {
    const frozen = wallet.status !== "active";
    const label = t("setup_wallet_option", {
      currency: wallet.currency,
      amount: formatCurrencyAmount(wallet.balance_available, wallet.currency, activeLanguage),
    });
    return {
      value: wallet.currency,
      label: frozen ? `${label} · ${t("setup_wallet_frozen")}` : label,
      disabled: frozen,
    };
  });

  const walletDescription = locked
    ? undefined
    : options.walletsLoading
      ? t("setup_wallet_loading")
      : options.walletsError
        ? options.walletsError
        : options.wallets.length === 0
          ? t("setup_wallet_empty")
          : undefined;

  return (
    <ColocaForm form={form} onSubmit={onSubmit} className="space-y-6">
      <ColocaSurface padding="lg" className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {isInternal && <OrganizationField />}
          <ColocaSelectField
            name="sourceCurrency"
            label={t("setup_wallet_label")}
            placeholder={t("setup_wallet_placeholder")}
            description={walletDescription}
            items={walletItems}
            disabled={locked || options.walletsLoading || isSubmitting}
          />
        </div>

        <CorridorChoiceField options={options.corridorOptions} disabled={locked || isSubmitting} />

        {sourceCurrency && corridorOption && sourceCurrency !== corridorOption.currency && (
          <CrossCurrencyNotice sourceCurrency={sourceCurrency} deliveryCurrency={corridorOption.currency} />
        )}

        <ColocaInputField
          name="concept"
          label={t("setup_concept_label")}
          description={t("setup_concept_description")}
          placeholder={t("setup_concept_placeholder")}
          maxLength={CONCEPT_MAX_LENGTH}
          disabled={locked || isSubmitting}
        />
      </ColocaSurface>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <TemplateDownloadCard
          corridor={corridor}
          onDownload={(country, rail) => void template.download(country, rail)}
          isDownloading={template.isDownloading}
          errorMessage={template.error ? describeError(template.error) : null}
        />
        <ColocaSurface padding="md">
          <BatchFileDropzone disabled={locked || isSubmitting} />
        </ColocaSurface>
      </div>

      {submitError && (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          heading={t("setup_upload_error_title")}
          text={describeError(submitError)}
        />
      )}

      <div className="flex justify-end">
        <ColocaButton
          type="submit"
          variant="primary"
          icon={ShieldCheck}
          loading={isSubmitting}
          disabled={locked || isSubmitting}
        >
          {isSubmitting ? t("setup_submitting") : t("setup_submit")}
        </ColocaButton>
      </div>
    </ColocaForm>
  );
}
